// @flow

import React, {useLayoutEffect, useRef, useState, forwardRef} from 'react';


import Portal from '@pb/kit-component-portal';
import {
    getOffsetTop,
    getOffsetLeft,
    getTransformOriginValue,
    getCoordinatesBox,
    checkFlipped
} from '@pb/kit-util-position-box';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {
    HorizontalPositionElement,
    VerticalPositionElement,
    Types,
    TRANSFORM_ORIGIN,
    ANCHOR_ORIGIN,
    INITIAL_FLIPPED
} from './constants';
import styles from './auto-position-box.module.styl';

import type {Node, Ref} from 'react';

import type {
    TransformOrigin,
    AnchorPosition,
    AnchorOrigin,
    PositionType,
    BoxPosition,
    Flipped,
    Style
} from './typedef';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

type Props = {|
    /**
     * An HTML element. It's used to set the position of the Box.
     * A ref that points to the used APB instance.
     */
    anchorRef: HTMLElement,
    /**
     * Component that needs to be positioned
     * @namespace
     * @property {object} params - The default values for configuration parameter.
     * @property {object} params.flipped - Indicates whether to flip the container.
     * @property {boolean} params.flipped.height - Flip to verticals.
     * @property {boolean} params.flipped.width - Flip to horizontals.
     */
    render: (params: { flipped: Flipped }) => Node,
    /**
    * The position of the element relative to the anchor vertically and horizontally
    * @namespace
    * @property {object} anchorOrigin - The default values for configuration parameter.
    * @property {string} anchorOrigin.vertical - The position of the element relative to the vertically.
    * @property {string} anchorOrigin.horizontal - The position of the element relative to the horizontally.
    */
    anchorOrigin?: AnchorOrigin,
    /**
    * Automatically creates width for the container
    */
    autoWidth?: boolean,
    /**
     * Component layout level.
     */
    layoutLevel?: number,
    /**
     * The flip modifier can change the placement of a Box when it's scheduled to overflow a given boundary.
     */
    notFlip?: boolean,
    /**
     * The position of the anchor relative to the element vertically and horizontally
     * @namespace
     * @property {object} transformOrigin - The default values for configuration parameter.
     * @property {string} transformOrigin.vertical - The position of the element relative to the vertically.
     * @property {string} transformOrigin.horizontal - The position of the element relative to the horizontally.
     */
    transformOrigin?: TransformOrigin,
    /**
     * Portal holder element ID
     */
    portalHolderId?: string,
    /**
    * Describes the positioning strategy to use. By default,
    * it is relative, which in the simplest cases does not require repositioning of the popper.
    */
    type?: PositionType,
|};

type ExtractReturnType = <R>(typeof forwardRef) => R;

/**
 * @see [PLTFUIKIT-45]{@link https://pitchbook.atlassian.net/browse/PLTFUIKIT-45}
 * @deprecated: Use Floating UI instead
 * */
export const AutoPositionBox: $Call<ExtractReturnType, any> = forwardRef<Props, HTMLElement>(function AutoPositionBox(
    props: Props,
    ref: Ref<*>
) {
    const {
        anchorOrigin = ANCHOR_ORIGIN,
        anchorRef,
        autoWidth = false,
        layoutLevel,
        notFlip = false,
        portalHolderId,
        render,
        transformOrigin = TRANSFORM_ORIGIN,
        type = Types.RELATIVE,
    } = props;
    const DEFAULT_STYLE = {visibility: 'hidden'};
    const ALLOWED_DEVIATION = 3;
    const portalRef = useRef<HTMLDivElement | null>(null);
    const [style, setStyle] = useState<Style>(DEFAULT_STYLE);
    const [flipped, setFlipped] = useState<Flipped>(INITIAL_FLIPPED);

    const isAbsolute = type === Types.ABSOLUTE;

    const getDisplayFlipped = (portalRect: ClientRect, anchorRect: ClientRect): Flipped => {
        const doc = document.documentElement || {};

        const width = doc.offsetWidth;
        const height = doc.offsetHeight;

        const isLeft = transformOrigin.horizontal === HorizontalPositionElement.LEFT;
        const isRight = transformOrigin.horizontal === HorizontalPositionElement.RIGHT;
        const isBottom = transformOrigin.vertical === VerticalPositionElement.BOTTOM;
        const isTop = transformOrigin.vertical === VerticalPositionElement.TOP;

        const offsetLeft = getOffsetLeft(anchorRect.width, anchorOrigin.horizontal, isRight);
        const offsetTop = getOffsetTop(anchorRect.height, anchorOrigin.vertical, isTop);

        return checkFlipped({
            isTop,
            isBottom,
            isLeft,
            isRight,
            anchorRect,
            portalRect,
            height,
            width,
            offsetTop,
            offsetLeft
        });
    };

    const getTransformOrigin = (portalRect: ClientRect, flip: Flipped): BoxPosition => ({
        vertical: getOffsetTop(portalRect.height, transformOrigin.vertical, flip.height),
        horizontal: getOffsetLeft(portalRect.width, transformOrigin.horizontal, flip.width)
    });

    const getAnchorOffset = (anchorRect: ClientRect, flip: Flipped): AnchorPosition => ({
        top: getOffsetTop(anchorRect.height, anchorOrigin.vertical, flip.height),
        left: getOffsetLeft(anchorRect.width, anchorOrigin.horizontal, flip.width)
    });

    const getPositioningStyle = (portal: HTMLDivElement) => {
        const portalRect = portal.getBoundingClientRect();
        const anchorRect = anchorRef.getBoundingClientRect();

        const flip = notFlip ? INITIAL_FLIPPED : getDisplayFlipped(portalRect, anchorRect);
        const elemTransformOrigin = getTransformOrigin(portalRect, flip);
        const anchorOffset = getAnchorOffset(anchorRect, flip);

        const {top, left} = getCoordinatesBox({
            isAbsolute,
            anchorRect,
            anchorOffset,
            elemTransformOrigin,
            isNeedScrollOffset: true
        });

        setFlipped(flip);

        if (autoWidth) {
            if (Math.abs(anchorRect.width - portalRect.width) <= ALLOWED_DEVIATION) {
                setStyle({
                    top: `${Math.round(top)}px`,
                    left: `${Math.round(left)}px`,
                    transformOrigin: getTransformOriginValue(elemTransformOrigin),
                    width: `${Math.round(anchorRect.width)}px`,
                    ...(layoutLevel ? {'--auto-position-box-z-index': Math.abs(layoutLevel)} : null)
                });
            } else {
                setStyle({
                    transformOrigin: getTransformOriginValue(elemTransformOrigin),
                    width: `${Math.round(anchorRect.width)}px`,
                    ...DEFAULT_STYLE,
                    ...(layoutLevel ? {'--auto-position-box-z-index': Math.abs(layoutLevel)} : null)
                });
            }
        } else {
            setStyle({
                top: `${Math.round(top)}px`,
                left: `${Math.round(left)}px`,
                transformOrigin: getTransformOriginValue(elemTransformOrigin),
                ...(layoutLevel ? {'--auto-position-box-z-index': Math.abs(layoutLevel)} : null)
            });
        }
    };

    const containerClasses = classNames(
        'auto-position-box__container', {
            'auto-position-box__container_absolute': isAbsolute
        }
    );

    const content = (
        <div className={classes['auto-position-box']} ref={ref}>
            { }
            <div ref={portalRef} className={containerClasses} style={style}>
                {render({flipped})}
            </div>
        </div>
    );

    useLayoutEffect(() => {
        const portal = portalRef.current;

        if (portal && anchorRef) {
            const handle = () => {
                getPositioningStyle(portal);
            };

            handle();

            const observer = new ResizeObserver(handle);

            document.addEventListener('scroll', handle, {capture: true});
            window.addEventListener('resize', handle);
            observer.observe(portal);
            observer.observe(anchorRef);

            return () => {
                document.removeEventListener('scroll', handle, {capture: true});
                window.removeEventListener('resize', handle);
                observer.unobserve(portal);
                observer.unobserve(anchorRef);
            };
        }
    }, [anchorRef]); // eslint-disable-line react-hooks/exhaustive-deps

    return isAbsolute ? (
        <Portal {...portalHolderId ? {holderId: portalHolderId} : null}>
            {content}
        </Portal>
    ) : content;
});

 AutoPositionBox.displayName = 'AutoPositionBox';

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
